import {
  ActivityHeader,
  AnimatedCardsFrame,
  CardMatching,
  ErrorBoundary,
  // FullscreenClipLoader,
  VideoPlayer,
} from '@components';
import { actionButtonLayoutByVariant } from '@components/ActivityModalLayout/ActivityModalLayout.layoutProps';
import { useScreenSize } from '@hooks';
import StatementCard from '@screens/CardsDefinitionsPage/components/StatementCard';
import { Button, Flex, theme, Typography } from '@styles';
import { bool, func, object, shape, string } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIndexedDB } from 'react-indexed-db-hook';
import { getAllImagesFromArticle, replaceUrls } from 'src/helpers';
import { useOnlineStatusDetector } from 'src/hooks/useOnlineStatusDetector';

import ChoosingSideLayout from './ChoosingSideLayout';
import ScenarioLayout from './ScenarioLayout';
import SituationLayout from './SituationLayout';
import SortLayout from './SortLayout';
import TestLayout from './TestLayout';

const ActivityLayout = ({
  activity,
  finalTest,
  // isLoading,
  isTranscriptShown,
  onFinish,
  openRankModal,
  setRankModalContent,
  type,
}) => {
  const { i18n } = useTranslation();
  const { isTabletSM } = useScreenSize();
  const { isOnline } = useOnlineStatusDetector();
  const { getAll } = useIndexedDB('image');

  const [downloadedArticleHTML, setDownloadedArticleHTML] = useState(null);
  const getArticleWithDownloadedImage = async (htmlString) => {
    if (htmlString) {
      const allSrcs = getAllImagesFromArticle(htmlString);
      const allImages = await getAll();
      const imageRecords = allImages.filter((img) => allSrcs.includes(img.url));
      setDownloadedArticleHTML(replaceUrls(htmlString, imageRecords));
    }
  };
  useEffect(() => {
    if (!isOnline && activity?.article?.html)
      getArticleWithDownloadedImage(activity?.article?.html);
  }, [isOnline, activity]);
  const getActivityTitle = () => {
    if (type === 'choose_correct_side_activity') {
      return (
        activity?.test?.title ||
        i18n.t(actionButtonLayoutByVariant?.[type]?.titleText)
      );
    }
    if (type === 'theory') {
      return i18n.t('ActivityTypes.theory');
    }
    if (activity?.[type]?.title) {
      return activity?.[type]?.title;
    }
    return i18n.t(actionButtonLayoutByVariant?.[type]?.titleText);
  };

  const renderActivityCard = useCallback(() => {
    switch (type) {
      case 'theory':
        return (
          <Flex flexDirection="column" gap={24}>
            {isTranscriptShown ? (
              <Typography
                color={theme.colors.white[100]}
                dangerouslySetInnerHTML={{
                  __html: activity?.theory?.subtitles_html,
                }}
                variant="MS-semibold-s16-lh25"
              />
            ) : (
              <ErrorBoundary>
                <VideoPlayer
                  dynamicHeight={478}
                  dynamicWidth="100%"
                  videosrc={activity?.theory?.video_url}
                />
              </ErrorBoundary>
            )}
            <Button
              onClick={onFinish}
              variant="secondary"
              width={isTabletSM ? '100%' : 290}
            >
              <Typography
                color={theme.colors.white[100]}
                variant="MS-semibold-s16-lh25"
              >
                {i18n
                  .t(
                    `LessonActivities.Theory.${isTranscriptShown ? 'FinishReading' : 'FinishWatching'}`
                  )
                  .toUpperCase()}
              </Typography>
            </Button>
          </Flex>
        );
      case 'article':
        return (
          <Flex flexDirection="column" gap={24}>
            <Typography
              color={theme.colors.white[100]}
              dangerouslySetInnerHTML={{
                __html: isOnline
                  ? activity?.article?.html
                  : downloadedArticleHTML || activity?.article?.html,
              }}
              style={{
                textIndent: '20px',
              }}
              variant="MS-regular-s16-lh25"
            />
            <Button
              onClick={onFinish}
              variant="secondary"
              width={isTabletSM ? '100%' : 290}
            >
              <Typography
                color={theme.colors.white[100]}
                variant="MS-semibold-s16-lh25"
              >
                {i18n.t('LessonActivities.Theory.FinishReading').toUpperCase()}
              </Typography>
            </Button>
          </Flex>
        );
      case 'match_activity':
        return (
          <CardMatching
            cards={activity?.match_activity?.items}
            onFinish={onFinish}
          />
        );
      case 'card_form_activity':
        return (
          <Flex flexDirection="column">
            <AnimatedCardsFrame
              data={activity?.card_form_activity?.cards}
              onFinish={onFinish}
            />
            <Flex flexDirection="column" gap={24} mt={64}>
              <Typography
                color={theme.colors.white[100]}
                mb={16}
                variant="MS-regular-s26-lh32"
              >
                {i18n.t('Activities.Cards.Terms')} (
                {activity?.card_form_activity?.cards?.length})
              </Typography>
              {activity?.card_form_activity?.cards?.map((card) => (
                <StatementCard
                  key={card?.id}
                  definition={card?.definition}
                  statement={card?.statement}
                />
              ))}
            </Flex>
          </Flex>
        );
      case 'situation':
        return (
          <SituationLayout
            onFinish={onFinish}
            situation={activity?.situation}
          />
        );
      case 'scenario':
        return (
          <ScenarioLayout onFinish={onFinish} scenario={activity?.scenario} />
        );
      case 'choose_correct_side_activity':
        return <ChoosingSideLayout onFinish={onFinish} test={activity?.test} />;
      case 'test':
        return (
          <TestLayout
            onFinish={onFinish}
            openRankModal={openRankModal}
            setRankModalContent={setRankModalContent}
            test={finalTest}
          />
        );
      case 'sorting_activity':
        return (
          <SortLayout onFinish={onFinish} sort={activity?.sorting_activity} />
        );
      default:
        return null;
    }
  }, [type, activity, i18n.language, finalTest?.title, downloadedArticleHTML]);

  // const renderLoader = () => <FullscreenClipLoader backgroundSource="" />;

  return (
    <Flex flex={1} flexDirection="column">
      <ActivityHeader
        activityVariant={type || 'theory'}
        containerProps={{ mb: 24 }}
        title={getActivityTitle()}
      />
      {renderActivityCard()}
    </Flex>
  );
};

ActivityLayout.propTypes = {
  activity: shape.isRequired,
  finalTest: object,
  // isLoading: bool.isRequired,
  isTranscriptShown: bool,
  onFinish: func.isRequired,
  openRankModal: func,
  setRankModalContent: func,
  type: string.isRequired,
};

ActivityLayout.defaultProps = {
  finalTest: null,
  isTranscriptShown: false,
  openRankModal: () => {},
  setRankModalContent: () => {},
};

export default ActivityLayout;
